<template>
  <div class="today-list">
    <ul class="list-unstyled" v-if="displayItems.length > 0">
      <li v-for="item in displayItems" :key="item.id">
        <img :src="item.image" alt="" />
        <div class="content">
          <router-link :to="item.pageUrl" class="link-dark description">{{ item.displayText }}</router-link>
          <div v-if="item.relationPath" class="relation-text">your {{ getRelationText(item.relationPath) }}</div>
        </div>
        <div class="weekday">{{ getWeekday(item) }}</div>
      </li>
    </ul>
    <div v-else class="no-events">No events on this day</div>
    <a v-if="remainingCount > 0" class="show-more" href="#" @click.prevent="showAll = true">
      show {{ remainingCount }} more
    </a>
  </div>
</template>

<style lang="scss" scoped>
ul {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 350px;
}

li {
  display: flex;
  line-height: 1;
  color: #333;

  img {
    margin-top: 0.25rem;
    margin-right: 0.75rem;
    height: 1.25rem;
    opacity: 40%;
  }

  .content {
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    gap: 0.25rem;
    flex: 1;
  }

  .relation-text {
    color: #bbb;
  }

  .weekday {
    font-size: 0.75rem;
    color: #999;
    margin-left: 0.5rem;
    align-self: flex-start;
    white-space: nowrap;
  }
}

.no-events {
  color: #999;
  font-size: 0.85rem;
  text-align: center;
  padding: 1rem 0;
}

.show-more {
  font-size: 0.8rem;
  color: #666;
  text-align: center;
  display: block;
  margin-top: 0.5rem;

  &:hover {
    color: #333;
  }
}
</style>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { TodayItem } from './TodayItem'
import { useRelationTextBuilder } from '@/rd/RelationPath'
import { DateTime } from 'luxon'

const props = defineProps<{
    items: TodayItem[]
}>()

const { getRelationText } = useRelationTextBuilder()

const showAll = ref(false)

const DISPLAY_LIMIT = 5

const displayItems = computed(() => {
    if (showAll.value || props.items.length <= DISPLAY_LIMIT)
        return props.items
    return props.items.slice(0, DISPLAY_LIMIT)
})

const remainingCount = computed(() => {
    if (showAll.value) return 0
    return Math.max(0, props.items.length - DISPLAY_LIMIT)
})

// Reset showAll when items change (different day selected)
watch(() => props.items, () => {
    showAll.value = false
})

function getWeekday(item: TodayItem) {
    const date = item.date
    if (!date.year || !date.month || !date.day) return ''
    try {
        return DateTime.utc(date.year, date.month, date.day).toFormat('EEE')
    } catch {
        return ''
    }
}
</script>
